import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios, { AxiosError } from 'axios';

import {
  ReportGroup,
  staticReportGroups,
  USE_BACKEND_API,
  apiConfig,
} from '../config/reportGroups';

const ALLOWED_CATEGORY_IDS = new Set(['btr', 'dlm', 'ingestion', 'vehicles']);

const filterToAllowedCategories = (groups: ReportGroup[]): ReportGroup[] => {
  const allowed = groups.filter((group) =>
    ALLOWED_CATEGORY_IDS.has(group.id.toLowerCase()),
  );

  if (allowed.length > 0) {
    return allowed;
  }

  const fallback = staticReportGroups.filter((group) =>
    ALLOWED_CATEGORY_IDS.has(group.id.toLowerCase()),
  );

  return fallback.length > 0 ? fallback : allowed;
};

interface ReportGroupsContextValue {
  reportGroups: ReportGroup[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const ReportGroupsContext = createContext<ReportGroupsContextValue | undefined>(
  undefined,
);

const buildAuthHeader = () => {
  if (!apiConfig.authToken) {
    return undefined;
  }

  if (apiConfig.authToken.toLowerCase().startsWith('bearer ')) {
    return apiConfig.authToken;
  }

  return `Bearer ${apiConfig.authToken}`;
};

export const ReportGroupsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [reportGroups, setReportGroups] = useState<ReportGroup[]>(
    USE_BACKEND_API ? [] : filterToAllowedCategories(staticReportGroups),
  );
  const [loading, setLoading] = useState<boolean>(USE_BACKEND_API);
  const [error, setError] = useState<string | null>(null);

  const fetchReportGroups = useCallback(async () => {
    if (!USE_BACKEND_API) {
      // Static configuration mode
      setReportGroups(filterToAllowedCategories(staticReportGroups));
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const authHeader = buildAuthHeader();
      const headers = authHeader ? { Authorization: authHeader } : {};

      const response = await axios.get(
        `${apiConfig.baseUrl}${apiConfig.reportGroupsEndpoint}`,
        { headers },
      );

      const groups: ReportGroup[] =
        response.data?.report_groups ?? response.data ?? [];

      setReportGroups(
        Array.isArray(groups)
          ? filterToAllowedCategories(groups)
          : filterToAllowedCategories([]),
      );
    } catch (err: unknown) {
      console.error('Failed to fetch report groups:', err);

      let message = 'Failed to load reports';
      if (axios.isAxiosError(err)) {
        const axiosError = err as AxiosError<{ detail?: string }>;
        if (axiosError.response?.status === 401) {
          message =
            'Authentication failed while contacting the reports API. Ensure REACT_APP_API_AUTH_TOKEN is set to a valid token.';
        } else if (axiosError.response?.data?.detail) {
          message = axiosError.response.data.detail;
        } else if (axiosError.message) {
          message = axiosError.message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }

      setError(message);
      setReportGroups(filterToAllowedCategories([]));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportGroups().catch((err) =>
      console.error('Unexpected error during report groups fetch:', err),
    );
  }, [fetchReportGroups]);

  const value = useMemo(
    () => ({
      reportGroups,
      loading,
      error,
      refresh: fetchReportGroups,
    }),
    [reportGroups, loading, error, fetchReportGroups],
  );

  return (
    <ReportGroupsContext.Provider value={value}>
      {children}
    </ReportGroupsContext.Provider>
  );
};

export const useReportGroups = (): ReportGroupsContextValue => {
  const context = useContext(ReportGroupsContext);

  if (!context) {
    throw new Error(
      'useReportGroups must be used within a ReportGroupsProvider',
    );
  }

  return context;
};
